import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Archive, RotateCcw, Search, UserPlus } from "lucide-react";
import { CreatePatientSchema, type CreatePatientInput } from "@clinic/types";
import { ageFromDob } from "@clinic/ui";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FieldError, Input, Label, Textarea } from "@/components/ui/input";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Table, TBody, Td, Th, THead, Tr } from "@/components/ui/table";

export function PatientsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const patients = useQuery({
    queryKey: ["patients", search, includeArchived],
    queryFn: () => api.patients.list({ search: search || undefined, includeArchived }),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.patients.archive(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["patients"] }),
  });
  const restore = useMutation({
    mutationFn: (id: string) => api.patients.restore(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["patients"] }),
  });

  const form = useForm<CreatePatientInput>({ resolver: zodResolver(CreatePatientSchema) });
  const create = useMutation({
    mutationFn: (input: CreatePatientInput) => api.patients.create(input),
    onSuccess: (patient) => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      setCreateOpen(false);
      form.reset();
      navigate({ to: "/patients/$patientId", params: { patientId: patient.id } });
    },
  });

  return (
    <div>
      <PageHeader
        title="Patients"
        subtitle="Search by name, ID number, phone or date of birth (yyyy-mm-dd)"
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <UserPlus size={15} /> New patient
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
          <Input
            className="pl-9"
            placeholder="Search patients…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="accent-[var(--accent)]"
          />
          Show archived
        </label>
      </div>

      {patients.isLoading ? (
        <SkeletonRows rows={6} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>Name</Th>
              <Th>ID number</Th>
              <Th>Age</Th>
              <Th>Phone</Th>
              <Th>Visits</Th>
              <Th>Status</Th>
              <Th className="w-12" />
            </Tr>
          </THead>
          <TBody>
            {(patients.data ?? []).map((p) => (
              <Tr
                key={p.id}
                className="cursor-pointer"
                onClick={() => navigate({ to: "/patients/$patientId", params: { patientId: p.id } })}
              >
                <Td className="font-medium">{p.fullName}</Td>
                <Td className="font-mono text-xs">{p.idNumber}</Td>
                <Td>{ageFromDob(p.dob)}</Td>
                <Td>{p.phone}</Td>
                <Td>{p._count?.eyeRecords ?? 0}</Td>
                <Td>
                  {p.archivedAt ? <Badge tone="cancelled">Archived</Badge> : <Badge tone="active">Active</Badge>}
                </Td>
                <Td onClick={(e) => e.stopPropagation()}>
                  {p.archivedAt ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Restore patient"
                      onClick={() => restore.mutate(p.id)}
                    >
                      <RotateCcw size={14} />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Archive patient"
                      onClick={() => {
                        if (confirm(`Archive ${p.fullName}? The file is kept and can be restored.`)) {
                          archive.mutate(p.id);
                        }
                      }}
                    >
                      <Archive size={14} />
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
            {(patients.data ?? []).length === 0 && (
              <Tr>
                <Td colSpan={7} className="py-10 text-center text-text-tertiary">
                  No patients found.
                </Td>
              </Tr>
            )}
          </TBody>
        </Table>
      )}

      {/* create patient */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent title="New patient" description="Create a patient file">
          <form
            onSubmit={form.handleSubmit((values) => create.mutate(values))}
            className="space-y-4"
          >
            <div>
              <Label>Full name</Label>
              <Input {...form.register("fullName")} placeholder="David Cohen" />
              <FieldError message={form.formState.errors.fullName?.message} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date of birth</Label>
                <Input type="date" {...form.register("dob")} />
                <FieldError message={form.formState.errors.dob?.message} />
              </div>
              <div>
                <Label>ID number</Label>
                <Input {...form.register("idNumber")} placeholder="045812937" />
                <FieldError message={form.formState.errors.idNumber?.message} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input {...form.register("phone")} placeholder="+972-50-555-0101" />
                <FieldError message={form.formState.errors.phone?.message} />
              </div>
              <div>
                <Label>Email (for reminders)</Label>
                <Input type="email" {...form.register("email")} placeholder="optional" />
                <FieldError message={form.formState.errors.email?.message} />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input {...form.register("address")} placeholder="optional" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea {...form.register("notes")} placeholder="Medical background, allergies…" />
            </div>
            {create.isError && (
              <p className="text-sm text-danger">
                {create.error instanceof Error ? create.error.message : "Failed to create patient"}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Create patient
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
